import {useCallback, useEffect, useRef, useState} from "react"
import {useNavigate} from "react-router-dom"
import {condosRtkApi, useLazyGetCondoPropertiesPageQuery} from "@/api/condo"
import {CondoPropertiesSortBy} from "@/types/Condo"
import {PropertyResponse as Property} from "@/types/Property"
import {usePaginatedList} from "@/hooks/usePaginatedList"
import {
   clearCondoPropertiesTableReturnState,
   readCondoPropertiesTableReturnState,
   saveCondoPropertiesTableReturnState,
} from "@/hooks/useCondoPropertiesTableReturn"

export const PROPERTY_TABLE_PAGE_SIZE = 10

export const propertySortLabels: Record<CondoPropertiesSortBy, string> = {
   "name,asc": "Alfabético A - Z",
   "salePrice,asc": "Precio menor venta",
   "salePrice,desc": "Precio mayor venta",
   "nightlyRent,asc": "Precio menor renta/noche",
   "nightlyRent,desc": "Precio mayor renta/noche",
   "annualRent,desc": "Precio mayor renta/año",
   "annualRent,asc": "Precio menor renta/año",
   "surface,asc": "m² menor",
   "surface,desc": "m² mayor",
   "rooms,desc": "Recámaras mayor",
   "rooms,asc": "Recámaras menor",
}

const isCondoPropertiesSortBy = (value: string): value is CondoPropertiesSortBy =>
   Object.keys(propertySortLabels).includes(value)

const getPropertyId = (property: Property) => property.id

export function useCondoPropertiesTable({condoId, returnTo}: {condoId: string; returnTo: string}) {
   const navigate = useNavigate()
   const [getPropertiesPage] = useLazyGetCondoPropertiesPageQuery()
   const prefetchPropertiesPage = condosRtkApi.usePrefetch("getCondoPropertiesPage")
   const returnStateRef = useRef(readCondoPropertiesTableReturnState())
   const restoreDoneRef = useRef(false)
   const targetLoadedCountRef = useRef(
      returnStateRef.current?.returnTo === returnTo
         ? Math.max(returnStateRef.current.visibleCount, PROPERTY_TABLE_PAGE_SIZE)
         : PROPERTY_TABLE_PAGE_SIZE,
   )

   const [sortOption, setSortOption] = useState<CondoPropertiesSortBy>(() => {
      const returnState = returnStateRef.current
      return returnState?.returnTo === returnTo && isCondoPropertiesSortBy(returnState.sortOption)
         ? returnState.sortOption
         : "name,asc"
   })

   const fetchPropertiesPage = useCallback(
      async ({pageSize, cursor}: {pageSize: number; cursor?: string}) =>
         getPropertiesPage(
            {
               condoId,
               pageSize,
               startAfter: cursor,
               sortBy: sortOption,
            },
            true,
         ).unwrap(),
      [condoId, getPropertiesPage, sortOption],
   )
   const {
      items: properties,
      totalCount,
      displayTotal,
      remainingCount,
      hasMore,
      isLoading,
      isLoadingAll,
      hasError,
      loadMore,
      loadAll,
   } = usePaginatedList<Property, string>({
      pageSize: PROPERTY_TABLE_PAGE_SIZE,
      fetchPage: fetchPropertiesPage,
      getCursor: getPropertyId,
      getItemKey: getPropertyId,
      resetKey: `${condoId}:${sortOption}`,
   })
   const loadedCount = properties.length

   useEffect(() => {
      if (isLoading || !hasMore || properties.length === 0) return

      const lastProperty = properties[properties.length - 1]
      prefetchPropertiesPage(
         {
            condoId,
            pageSize: PROPERTY_TABLE_PAGE_SIZE,
            startAfter: lastProperty.id,
            sortBy: sortOption,
         },
         {ifOlderThan: 60},
      )
   }, [condoId, hasMore, isLoading, prefetchPropertiesPage, properties, sortOption])

   useEffect(() => {
      restoreDoneRef.current = false
   }, [condoId, sortOption])

   const changeSort = (nextSortOption: CondoPropertiesSortBy) => {
      clearCondoPropertiesTableReturnState()
      returnStateRef.current = null
      setSortOption(nextSortOption)
   }

   const openProperty = (property: Property) => {
      saveCondoPropertiesTableReturnState({
         returnTo,
         selectedPropertyId: property.id,
         scrollY: window.scrollY,
         visibleCount: loadedCount,
         sortOption,
         createdAt: Date.now(),
      })

      navigate(`/inmuebles/${property.id}`, {
         state: {
            property,
            fromSearch: {
               source: "condo",
               returnTo,
            },
         },
      })
   }

   useEffect(() => {
      const returnState = returnStateRef.current
      if (
         restoreDoneRef.current ||
         isLoading ||
         !returnState ||
         returnState.returnTo !== returnTo ||
         loadedCount === 0
      ) {
         return
      }

      const selectedRowIsLoaded = properties.some(
         (property) => property.id === returnState.selectedPropertyId,
      )
      const loadedPreviousAmount = loadedCount >= targetLoadedCountRef.current
      const reachedEnd = totalCount !== null && loadedCount >= totalCount

      if (!selectedRowIsLoaded && !loadedPreviousAmount && !reachedEnd) {
         loadMore()
         return
      }

      let frameId = 0
      let attempts = 0
      const maxAttempts = 20

      const finishRestore = () => {
         clearCondoPropertiesTableReturnState()
         returnStateRef.current = null
         restoreDoneRef.current = true
      }

      const scrollToSavedRow = () => {
         const selectedRow = document.querySelector(
            `[data-property-id="${returnState.selectedPropertyId}"]`,
         )

         if (selectedRow instanceof HTMLElement) {
            selectedRow.scrollIntoView({behavior: "auto", block: "center"})
            finishRestore()
            return
         }

         attempts += 1
         if (attempts >= maxAttempts) {
            window.scrollTo({top: returnState.scrollY, behavior: "auto"})
            finishRestore()
            return
         }

         frameId = window.requestAnimationFrame(scrollToSavedRow)
      }

      frameId = window.requestAnimationFrame(scrollToSavedRow)
      return () => window.cancelAnimationFrame(frameId)
   }, [isLoading, loadMore, loadedCount, properties, returnTo, totalCount])

   return {
      properties,
      displayTotal,
      remainingCount,
      hasMore,
      isLoading,
      isLoadingAll,
      hasError,
      sortOption,
      changeSort,
      loadMore,
      loadAll,
      openProperty,
   }
}
